import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useToast } from '@missionlabs/react'
import {
    $createRangeSelection,
    $getRoot,
    $getSelection,
    $insertNodes,
    $setSelection
} from 'lexical'
import { FC, useEffect } from 'react'
import { useWhatsAppSendMessage } from 'shared/hooks/useWhatsAppSendMessage'

import { $createUploadNode } from '../nodes/UploadNode'

export interface WhatsAppUploadPluginProps {}

export interface WhatsAppUploadEventDetail {
    ID: string
    mime: string
    body: string
    fileName: string
    channelID: string
    userID: string
}

export interface WhatsAppUpdateUploadEventDetail {
    ID: string
    eventType: 'REMOVE' | 'COMPLETE'
    uploadID?: string
}

export const ADD_WHATSAPP_UPLOAD_EVENT = 'ADD_WHATSAPP_UPLOAD_EVENT'
export const UPDATE_WHATSAPP_UPLOAD_EVENT = 'UPDATE_WHATSAPP_UPLOAD_EVENT'

export function isFileTooLarge(file: File): string | void {
    const maxFileSizes = {
        image: 5 * 1024 * 1024, // 5 MB
        video: 16 * 1024 * 1024, // 16 MB
        audio: 16 * 1024 * 1024, // 16 MB
        document: 100 * 1024 * 1024 // 100 MB
    }

    if (!file?.size) return

    const fileType = file.type.split('/')[0]
    const maxFileSize = maxFileSizes[fileType]
    if (!maxFileSize) return

    if (file.size > maxFileSize) {
        const prettySize = (size: number) => {
            if (size < 1024) return `${size} Bytes`
            if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`
            return `${(size / (1024 * 1024)).toFixed(2)} MB`
        }
        return `File size ${prettySize(
            file.size
        )} exceeds the maximum allowed limit of ${prettySize(maxFileSize)} for ${fileType}s`
    }
}

export const dispatchWhatsAppUploadEvent = (detail: WhatsAppUploadEventDetail) => {
    return document.dispatchEvent(new CustomEvent(ADD_WHATSAPP_UPLOAD_EVENT, { detail }))
}

export const dispatchWhatsAppUpdateUploadEvent = (
    eventType: 'REMOVE' | 'COMPLETE',
    ID: string,
    uploadID?: string
) => {
    return document.dispatchEvent(
        new CustomEvent(UPDATE_WHATSAPP_UPLOAD_EVENT, { detail: { eventType, ID, uploadID } })
    )
}

export const WhatsAppUploadPlugin: FC<WhatsAppUploadPluginProps> = () => {
    const [editor] = useLexicalComposerContext()
    const { toast } = useToast()
    const { dispatch } = useWhatsAppSendMessage()

    useEffect(() => {
        const insertUpload = (e: Event) => {
            const event = e as CustomEvent<WhatsAppUploadEventDetail>
            const { ID, mime, body, fileName } = event.detail

            editor.update(() => {
                const currSelection = $getSelection()
                const rangeSelection = $createRangeSelection()
                $setSelection(rangeSelection)

                // Insert upload node to show preview
                $insertNodes([
                    $createUploadNode(ID, mime, body, fileName, false, undefined, removeID => {
                        dispatchWhatsAppUpdateUploadEvent('REMOVE', removeID)
                    })
                ])
                $setSelection(currSelection)
            })

            // Update the draft message with attachment data
            dispatch({
                type: 'SET_MESSAGE',
                draftMessage: {
                    attachment: { file: body, mime }
                }
            })

            // Mark upload as complete
            editor.update(() => {
                const root = $getRoot()
                const nodes = root.getChildren()
                const existingUpload = nodes.find(n => n.__type === 'upload' && n.__ID === ID)

                if (existingUpload) {
                    existingUpload.replace(
                        $createUploadNode(
                            existingUpload.__ID,
                            existingUpload.__mime,
                            existingUpload.__body,
                            existingUpload.__fileName,
                            true,
                            undefined,
                            removeID => {
                                dispatchWhatsAppUpdateUploadEvent('REMOVE', removeID)
                            }
                        )
                    )
                }
            })
        }

        const updateUpload = (e: Event) => {
            const event = e as CustomEvent<WhatsAppUpdateUploadEventDetail>
            const { ID, eventType, uploadID } = event.detail

            editor.update(() => {
                const root = $getRoot()
                const nodes = root.getChildren()
                const existingUpload = nodes.find(n => n.__type === 'upload' && n.__ID === ID)

                if (!existingUpload) return
                if (eventType === 'REMOVE') {
                    // Clear attachment when file is removed
                    dispatch({
                        type: 'SET_MESSAGE',
                        draftMessage: {
                            attachment: undefined
                        }
                    })
                    return existingUpload.remove()
                }
                if (eventType === 'COMPLETE') {
                    existingUpload.replace(
                        $createUploadNode(
                            existingUpload.__ID,
                            existingUpload.__mime,
                            existingUpload.__body,
                            existingUpload.__fileName,
                            true,
                            uploadID,
                            removeID => {
                                dispatchWhatsAppUpdateUploadEvent('REMOVE', removeID)
                            }
                        )
                    )
                }
            })
        }

        document.addEventListener(ADD_WHATSAPP_UPLOAD_EVENT, insertUpload)
        document.addEventListener(UPDATE_WHATSAPP_UPLOAD_EVENT, updateUpload)

        return () => {
            document.removeEventListener(ADD_WHATSAPP_UPLOAD_EVENT, insertUpload)
            document.removeEventListener(UPDATE_WHATSAPP_UPLOAD_EVENT, updateUpload)
        }
    }, [editor, toast, dispatch])

    return null
}
